/**
 * Vertex group + material slot CRUD actions for the edit slice.
 *
 * Every mutation goes through a single `patchVertexGroups` /
 * `patchMaterialSlots` pipeline that owns undo semantics and the
 * non-empty-slot / empty-list-omission invariants.
 */

import {
  deriveFacesFromVertices,
  deriveVerticesFromFaces,
} from "../../modeling/editSelection.ts";
import type {
  FaceAttributeTable,
  MaterialSlot,
  VertexGroup,
} from "../../modeling/types.ts";
import {
  createMaterialSlotId,
  normalizeFaceAttributes,
} from "../../modeling/types.ts";
import { pruneUnusedMaterials } from "../materialOps.ts";
import { patchSceneObject } from "../sceneObjectMap.ts";
import type { AppState } from "../storeTypes.ts";
import { captureSnapshot, undoManager } from "../storeUndo.ts";
import type { Get_, Set_ } from "./core.ts";
import {
  applyVertexSelectionMode,
  type VertexSelectionMode,
} from "./selectionOps.ts";

// ---------------------------------------------------------------------------
// Vertex group helpers (private)
// ---------------------------------------------------------------------------

/** Produce a Blender-style unique default name ("Group", "Group.001", …). */
function nextDefaultVertexGroupName(groups: readonly VertexGroup[]): string {
  const base = "Group";
  const used = new Set(groups.map((g) => g.name));
  if (!used.has(base)) return base;
  for (let n = 1; n < 10_000; n++) {
    const name = `${base}.${String(n).padStart(3, "0")}`;
    if (!used.has(name)) return name;
  }
  return `${base}.${Date.now()}`;
}

let vertexGroupIdCounter = 0;
function makeVertexGroupId(): string {
  vertexGroupIdCounter += 1;
  return `vg_${vertexGroupIdCounter}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * The single mutation path for `SceneObject.vertexGroups` — captures a
 * snapshot for undo, runs `updater`, and patches the object, collapsing
 * an empty list to `undefined` so the "omit when empty" invariant holds.
 */
function patchVertexGroups(
  get: Get_,
  set: Set_,
  objectId: string,
  updater: (groups: VertexGroup[]) => VertexGroup[],
  editModePatch?: (em: AppState["editMode"]) => AppState["editMode"],
): void {
  const obj = get().sceneObjects.get(objectId);
  if (!obj) return;
  undoManager.push(captureSnapshot(get()));
  const next = updater(obj.vertexGroups ?? []);
  const state = get();
  set({
    sceneObjects: patchSceneObject(state.sceneObjects, objectId, {
      vertexGroups: next.length > 0 ? next : undefined,
    }),
    ...(editModePatch ? { editMode: editModePatch(state.editMode) } : {}),
    canUndo: undoManager.canUndo,
    canRedo: undoManager.canRedo,
  });
}

/**
 * Single snapshot + patch pipeline for `materialSlots` mutations —
 * parallels `patchVertexGroups`. Refuses updaters that would leave the
 * slot list empty (a SceneObject must always carry ≥1 slot).
 */
function patchMaterialSlots(
  get: Get_,
  set: Set_,
  objectId: string,
  updater: (slots: MaterialSlot[]) => MaterialSlot[],
  extraPatch?: Partial<AppState>,
): void {
  const obj = get().sceneObjects.get(objectId);
  if (!obj) return;
  const next = updater(obj.materialSlots);
  if (next.length === 0) return;
  undoManager.push(captureSnapshot(get()));
  const state = get();
  const nextSceneObjects = patchSceneObject(state.sceneObjects, objectId, {
    materialSlots: next,
  });
  const prunedMats = pruneUnusedMaterials(state.materials, nextSceneObjects);
  set({
    sceneObjects: nextSceneObjects,
    ...(prunedMats ? { materials: prunedMats } : {}),
    ...(extraPatch ?? {}),
    canUndo: undoManager.canUndo,
    canRedo: undoManager.canRedo,
  });
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createGroupOps(set: Set_, get: Get_) {
  return {
    // ---- Vertex groups ----

    createVertexGroup: (objectId: string, name?: string): string | null => {
      const obj = get().sceneObjects.get(objectId);
      if (!obj) return null;
      const id = makeVertexGroupId();
      const finalName =
        name?.trim() || nextDefaultVertexGroupName(obj.vertexGroups ?? []);
      // Activate the new group so the user's next action (Assign / Select)
      // targets it without an extra click — matches Blender's behaviour.
      patchVertexGroups(
        get,
        set,
        objectId,
        (groups) => [...groups, { id, name: finalName, indices: new Set() }],
        (em) =>
          em && em.objectId === objectId
            ? { ...em, activeVertexGroupId: id }
            : em,
      );
      return id;
    },

    deleteVertexGroup: (objectId: string, groupId: string) => {
      const obj = get().sceneObjects.get(objectId);
      if (!obj) return;
      undoManager.push(captureSnapshot(get()));
      const nextGroups = (obj.vertexGroups ?? []).filter(
        (g) => g.id !== groupId,
      );
      const state = get();
      set({
        sceneObjects: patchSceneObject(state.sceneObjects, objectId, {
          vertexGroups: nextGroups.length > 0 ? nextGroups : undefined,
        }),
        ...(state.editMode &&
        state.editMode.objectId === objectId &&
        state.editMode.activeVertexGroupId === groupId
          ? { editMode: { ...state.editMode, activeVertexGroupId: null } }
          : {}),
        canUndo: undoManager.canUndo,
        canRedo: undoManager.canRedo,
      });
    },

    renameVertexGroup: (objectId: string, groupId: string, name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      patchVertexGroups(get, set, objectId, (groups) =>
        groups.map((g) => (g.id === groupId ? { ...g, name: trimmed } : g)),
      );
    },

    setActiveVertexGroup: (groupId: string | null) => {
      const em = get().editMode;
      if (!em) return;
      if ((em.activeVertexGroupId ?? null) === groupId) return;
      set({ editMode: { ...em, activeVertexGroupId: groupId } });
    },

    assignSelectionToGroup: (objectId: string, groupId: string) => {
      const em = get().editMode;
      if (!em || em.objectId !== objectId) return;
      // `selectedVertices` is kept authoritative across all granularities
      // (in face mode it's derived from selectedFaces on every mutation —
      // see CLAUDE.md "derive vertices from faces"), so using it directly
      // Does The Right Thing in vertex/edge/face mode without re-resolution.
      const sel = em.selectedVertices;
      if (sel.size === 0) return;
      patchVertexGroups(get, set, objectId, (groups) =>
        groups.map((g) =>
          g.id === groupId
            ? { ...g, indices: new Set([...g.indices, ...sel]) }
            : g,
        ),
      );
    },

    removeSelectionFromGroup: (objectId: string, groupId: string) => {
      const em = get().editMode;
      if (!em || em.objectId !== objectId) return;
      const sel = em.selectedVertices;
      if (sel.size === 0) return;
      patchVertexGroups(get, set, objectId, (groups) =>
        groups.map((g) => {
          if (g.id !== groupId) return g;
          const next = new Set(g.indices);
          for (const v of sel) next.delete(v);
          return { ...g, indices: next };
        }),
      );
    },

    selectVertexGroup: (
      objectId: string,
      groupId: string,
      mode: VertexSelectionMode,
    ) => {
      const obj = get().sceneObjects.get(objectId);
      const em = get().editMode;
      if (!obj || !em || em.objectId !== objectId) return;
      // Curves use `curveEditVertices`, not `selectedVertices`, and cannot
      // own vertex groups. Guard defensively even though the panel hides
      // itself for curves — keeps the action safe to call from anywhere.
      if (em.isCurve) return;
      const group = obj.vertexGroups?.find((g) => g.id === groupId);
      if (!group) return;
      // Compute next vertex selection via the shared selection-mode reducer,
      // then reconcile face/edge state through the same path used by every
      // other selection entrypoint — never hand-roll face inference here
      // (see CLAUDE.md: "Never infer face selection from vertex membership").
      const nextVerts = applyVertexSelectionMode(
        em.selectedVertices,
        group.indices,
        mode,
      );
      if (em.granularity === "face") {
        // Face mode is authoritative: derive faces via the shared helper,
        // then re-derive vertices strictly from those faces so the face-mode
        // invariant holds (CLAUDE.md "derive vertices from faces").
        const nextFaces = deriveFacesFromVertices(obj.topology, nextVerts);
        const derived = deriveVerticesFromFaces(obj.topology, nextFaces);
        set({
          editMode: {
            ...em,
            selectedVertices: derived,
            selectedFaces: nextFaces,
            selectedEdges: new Set(),
            activeVertexGroupId: groupId,
          },
        });
        return;
      }
      set({
        editMode: {
          ...em,
          selectedVertices: nextVerts,
          selectedFaces: new Set(),
          selectedEdges: new Set(),
          activeVertexGroupId: groupId,
        },
      });
    },

    // ---- Material slots ----

    addMaterialSlot: (objectId: string) => {
      const obj = get().sceneObjects.get(objectId);
      if (!obj) return;
      const newMatId = get().createMaterial();
      const newSlot: MaterialSlot = {
        id: createMaterialSlotId(),
        materialId: newMatId,
      };
      patchMaterialSlots(
        get,
        set,
        objectId,
        (slots) => [...slots, newSlot],
        // Auto-activate the new slot so the picker and vertex-group binding
        // target it immediately — matches vertex groups' auto-activation.
        { activeMaterialSlotId: newSlot.id },
      );
    },

    removeMaterialSlot: (objectId: string, slotId: string) => {
      const obj = get().sceneObjects.get(objectId);
      if (!obj) return;
      if (obj.materialSlots.length <= 1) return; // SceneObject must keep ≥1 slot
      const removeIdx = obj.materialSlots.findIndex((s) => s.id === slotId);
      if (removeIdx < 0) return;
      const wasActive = get().activeMaterialSlotId === slotId;

      // Per-face indices need rewriting alongside the slot list: faces
      // previously assigned to the removed slot fall back to slot 0; faces
      // assigned to slots after `removeIdx` shift down by one. Both patches
      // share one undo snapshot — the user sees a single atomic edit.
      undoManager.push(captureSnapshot(get()));
      const nextSlots = obj.materialSlots.filter((s) => s.id !== slotId);
      const nextFaceAttrs = remapMaterialIndexAfterSlotRemoval(
        obj.faceAttributes,
        removeIdx,
      );
      const state = get();
      const nextSceneObjects = patchSceneObject(state.sceneObjects, objectId, {
        materialSlots: nextSlots,
        faceAttributes: nextFaceAttrs,
      });
      const prunedMats = pruneUnusedMaterials(
        state.materials,
        nextSceneObjects,
      );
      set({
        sceneObjects: nextSceneObjects,
        ...(prunedMats ? { materials: prunedMats } : {}),
        ...(wasActive ? { activeMaterialSlotId: null } : {}),
        canUndo: undoManager.canUndo,
        canRedo: undoManager.canRedo,
      });
    },

    setActiveMaterialSlot: (slotId: string | null) => {
      if (get().activeMaterialSlotId === slotId) return;
      set({ activeMaterialSlotId: slotId });
    },
  };
}

/**
 * Remap `faceAttributes.materialIndex` after a slot at `removedIdx` is
 * deleted. Faces assigned to the removed slot fall back to slot 0; faces
 * assigned to later slots shift down by one. Returns a normalized
 * `FaceAttributeTable` or `undefined` if all per-face data collapses.
 */
function remapMaterialIndexAfterSlotRemoval(
  attrs: FaceAttributeTable | undefined,
  removedIdx: number,
): FaceAttributeTable | undefined {
  if (!attrs?.materialIndex) return attrs;
  const src = attrs.materialIndex;
  const out = new Uint16Array(src.length);
  let anyNonZero = false;
  for (let i = 0; i < src.length; i++) {
    const v = src[i];
    let mapped: number;
    if (v === removedIdx) mapped = 0;
    else if (v > removedIdx) mapped = v - 1;
    else mapped = v;
    out[i] = mapped;
    if (mapped !== 0) anyNonZero = true;
  }
  return normalizeFaceAttributes({
    ...attrs,
    materialIndex: anyNonZero ? out : undefined,
  });
}
